import { ResourceType } from "@/game/entities/config/resources";
import useEmitter, { EmitterEvents } from "@/hooks/useEmitter";
import useSiegeSystemRepository from "@/hooks/useSiegeSystemRepository";
import useBuildingsStorageStore from "@/store/useBuildingsStorageStore";
import {
	PopupDictionary,
	PopupType,
	usePopupStore,
} from "@/store/usePopupStore";
import useQuickToolStore from "@/store/useQuickToolStore";
import useResourcesStore from "@/store/useResourcesStore";
import useSettingsStore from "@/store/useSettingsStore";
import useSettlementsStore from "@/store/useSettlementsStore";
import useSiegeSystemStore from "@/store/useSiegeSystemStore";
import useUpgradesStore from "@/store/useUpgradesStore";
import { TileAction } from "@/types/tile";
import { SkinTypes, StatusTypes } from "@/types/ui";
import { QuickToolActions, quickToolActionsSalvage } from "@/types/upgrades";
import { translate } from "@/utils/translate";
import {
	FrameName,
	tilerepoBanditsFrameNames,
	tilerepoBigFishingBoatsFrameNames,
	tilerepoBlastFurnacesFrameNames,
	tilerepoBridgeRoadsFrameNames,
	tilerepoCobbledRoadsFrameNames,
	tilerepoDirtRoadsFrameNames,
	tilerepoFishingBoatsFrameNames,
	tilerepoFishTrapsFrameNames,
	tilerepoFordCrossingRoadsFrameNames,
	tilerepoForestFrameNames,
	tilerepoFriendlyUnitsFrameNames,
	tilerepoGrasslandFrameNames,
	tilerepoGravelRoadsFrameNames,
	tilerepoLightHousesFrameNames,
	tilerepoLookoutTowersFrameNames,
	tilerepoMercenariesFrameNames,
	tilerepoPiratesFrameNames,
	tilerepoPremiumBuildings,
	tilerepoQuickToolFrameNames,
	tilerepoRiversFrameNames,
	tilerepoSalvageableFrameNames,
	tileRepoScoutTowerFrameNames,
	tilerepoSettlementCastleBuildingsFrameNames,
	tilerepoStoneBridgeRoadsFrameNames,
	tilerepoTunnelRoadsFrameNames,
	tilerepoWoodenBridgeRoadsFrameNames,
} from "../entities/config";
import { isBuilding } from "../entities/config/buildings";
import { isRoad } from "../entities/config/roads";
import { UpgradeIds } from "../entities/config/upgrades";
import type AbstractSalvageableTileEntity from "../entities/salvagables/AbstractSalvageableTileEntity";
import type TileEntity from "../entities/TileEntity";
import type TileRepository from "../TileRepository";

const getTerrainEntityBelow = (
	tilerepo: TileRepository,
	tile: TileEntity,
): TileEntity | null => {
	const [getTerrainEntity] = tilerepo.getTileEntityFactoriesByTileIndex(
		tile.index,
	);
	return getTerrainEntity();
};

const canBuildOnTerrainBelow = (
	tilerepo: TileRepository,
	tile: TileEntity,
	building: FrameName,
): boolean => {
	const terrainEntity = getTerrainEntityBelow(tilerepo, tile);
	return terrainEntity?.canUpgradeTo.has(building) ?? false;
};

export const manageQuickTool = (
	tilerepo: TileRepository,
	clickedTile: TileEntity,
): void => {
	const {
		action,
		building,
		active,
		highwayStartCoordinates,
		setBuilding,
		setActive,
		setShow,
		setError,
		setAction,
		setHighwayStartCoordinates,
	} = useQuickToolStore();
	if (!active) {
		setShow(false);
		setAction(QuickToolActions.NONE);
		return;
	}

	let selectedTile = tilerepo.getTileEntityByTileCoordinates({
		x: clickedTile.x,
		y: clickedTile.y,
	});

	if (
		(action === QuickToolActions.DEMOLISH ||
			action === QuickToolActions.PUT_IN_INVENTORY) &&
		selectedTile?.frameName === FrameName.GEMS_MINE
	)
		return;
	if (selectedTile) {
		if (
			useSiegeSystemStore().movingUnitsIndex !== null &&
			tilerepoFriendlyUnitsFrameNames.has(building) &&
			selectedTile.canUpgradeTo.has(building) &&
			selectedTile.isBlockedByEnemy
		) {
			setBuilding(FrameName.NOT_SET);
			setActive(false);
			setShow(false);
			setError(false);
			setAction(QuickToolActions.NONE);
			setHighwayStartCoordinates(null);
			useSiegeSystemRepository().moveUnit(building, {
				x: selectedTile.x,
				y: selectedTile.y,
			});
			return;
		}
		const prevFrameName = selectedTile.frameName;

		if (prevFrameName === undefined) return;

		let nextBuilding = building;

		if (action === QuickToolActions.BUILD) {
			if (tilerepoSettlementCastleBuildingsFrameNames.has(prevFrameName))
				return;
			if (isRoad(prevFrameName)) {
				// Toto sluzi ako bypass pre stavanie budov na cestach, nakolko povodna logika ma hardkodnute moznosti len pre stavanie ciest na cestach.
				const isBuildingOnRoadAction =
					nextBuilding &&
					!isRoad(nextBuilding) &&
					nextBuilding !== prevFrameName &&
					nextBuilding !== FrameName.NOT_SET &&
					selectedTile.upgrades.has(nextBuilding);

				const isCobbledRoad =
					tilerepoCobbledRoadsFrameNames.has(prevFrameName);
				const isGravelRoad =
					tilerepoGravelRoadsFrameNames.has(prevFrameName);
				const isDirtRoad =
					tilerepoDirtRoadsFrameNames.has(prevFrameName);

				if (isCobbledRoad && !isBuildingOnRoadAction) return;
				if (
					isGravelRoad &&
					!tilerepoCobbledRoadsFrameNames.has(building) &&
					!isBuildingOnRoadAction
				)
					return;
				if (
					isDirtRoad &&
					!tilerepoCobbledRoadsFrameNames.has(building) &&
					!tilerepoGravelRoadsFrameNames.has(building) &&
					!isBuildingOnRoadAction
				)
					return;

				const isCobbledBridge =
					tilerepoStoneBridgeRoadsFrameNames.has(prevFrameName);
				const isGravelBridge =
					tilerepoWoodenBridgeRoadsFrameNames.has(prevFrameName);
				const isDirtBridge =
					tilerepoFordCrossingRoadsFrameNames.has(prevFrameName);

				if (isCobbledBridge && !isBuildingOnRoadAction) return;
				if (
					isGravelBridge &&
					tilerepoCobbledRoadsFrameNames.has(building)
				) {
					nextBuilding = FrameName.STONE_BRIDGE;
				}
				if (isDirtBridge) {
					if (tilerepoCobbledRoadsFrameNames.has(building))
						nextBuilding = FrameName.STONE_BRIDGE;
					if (tilerepoGravelRoadsFrameNames.has(building))
						nextBuilding = FrameName.WOODEN_BRIDGE;
				}
			}
			if (
				tilerepoRiversFrameNames.has(prevFrameName) &&
				isRoad(building)
			) {
				const isCobbledBridge =
					tilerepoCobbledRoadsFrameNames.has(building);
				const isGravelBridge =
					tilerepoGravelRoadsFrameNames.has(building);
				const isDirtBridge = tilerepoDirtRoadsFrameNames.has(building);

				if (isCobbledBridge) nextBuilding = FrameName.STONE_BRIDGE;
				if (isGravelBridge) nextBuilding = FrameName.WOODEN_BRIDGE;
				if (isDirtBridge) nextBuilding = FrameName.FORD_CROSSING;
			}

			if (isRoad(building)) {
				if (prevFrameName === FrameName.HILLS)
					nextBuilding = FrameName.HILLY_ROAD;
				else if (prevFrameName === FrameName.MOUNTAINS)
					nextBuilding = FrameName.MOUNTAIN_ROAD;
			}

			if (nextBuilding === FrameName.DEEP_SEA_FISHING_BOAT) {
				// Handle special case for deep sea fishing boat
				nextBuilding = FrameName.SHALLOW_SEA_FISHING_BOAT;
			}

			if (nextBuilding === FrameName.DEEP_SEA_BIG_FISHING_BOAT) {
				// Handle special case for deep sea fishing boat
				nextBuilding = FrameName.SHALLOW_SEA_BIG_FISHING_BOAT;
			}

			[
				tilerepoFishingBoatsFrameNames,
				tilerepoBigFishingBoatsFrameNames,
				tilerepoFishTrapsFrameNames,
				tilerepoLightHousesFrameNames,
				tileRepoScoutTowerFrameNames,
				tilerepoBlastFurnacesFrameNames,
				tilerepoLookoutTowersFrameNames,
			].forEach((variant: Set<FrameName>): void => {
				if (variant.has(nextBuilding)) {
					// compare canUpgrateTo with variant options and choose the one that matches
					const upgradeTargets = tilerepoSalvageableFrameNames.has(
						prevFrameName,
					)
						? (getTerrainEntityBelow(tilerepo, selectedTile!)
								?.canUpgradeTo ?? new Set<FrameName>())
						: (selectedTile?.canUpgradeTo ?? new Set<FrameName>());
					const possibleVariants = Array.from(upgradeTargets).filter(
						(frame: FrameName) => variant.has(frame),
					);
					if (possibleVariants && possibleVariants.length > 0) {
						nextBuilding = possibleVariants[0];
					}
				}
			});

			const shipyard = tilerepo.getAShipyardEntity()!;
			const availableFishingBoats = shipyard?.availableFishingBoats ?? 0;

			const seaFrames = new Set<FrameName>([
				FrameName.SHALLOW_SEA_SMALL_FISH,
				FrameName.SHALLOW_SEA_MEDIUM_FISH,
				FrameName.DEEP_SEA_MEDIUM_FISH,
				FrameName.DEEP_SEA_BIG_FISH,
				FrameName.SHALLOW_SEA,
				FrameName.DEEP_SEA,
			]);

			const boatsFrames = new Set<FrameName>([
				FrameName.SHALLOW_SEA_FISHING_BOAT,
				FrameName.DEEP_SEA_FISHING_BOAT,
				FrameName.SHALLOW_SEA_BIG_FISHING_BOAT,
				FrameName.DEEP_SEA_BIG_FISHING_BOAT,
			]);

			const boatStorageFull =
				boatsFrames.has(nextBuilding) &&
				seaFrames.has(prevFrameName) &&
				availableFishingBoats <= 0;

			if (
				!(
					tilerepoSalvageableFrameNames.has(prevFrameName) &&
					isRoad(nextBuilding)
				) &&
				boatStorageFull
			) {
				useEmitter().emit(EmitterEvents.PhaserWarningMessage, {
					x: selectedTile!.x,
					y: selectedTile!.y,
					text: "ui.build_more_shipyards",
				});
				return;
			}

			//identifikacia toho, ci mozem alebo nemozem stavat strkove/dlayobne cesty
			//na yaklade poctu miest urciteho levelu
			let numberOfRoadsOnMap: {
				[FrameName.GRAVEL_ROAD]: number;
				[FrameName.COBBLED_ROAD]: number;
			};
			let userRoadCap: {
				[FrameName.GRAVEL_ROAD]: number;
				[FrameName.COBBLED_ROAD]: number;
			};

			let calculation: number = 0;

			if (
				tilerepoGravelRoadsFrameNames.has(nextBuilding) ||
				tilerepoWoodenBridgeRoadsFrameNames.has(nextBuilding)
			) {
				numberOfRoadsOnMap = tilerepo.getCurrentNumberOfPremiumRoads();
				userRoadCap = useSettlementsStore().getTotalAvailableRoadCaps;
				calculation =
					userRoadCap[FrameName.GRAVEL_ROAD] -
					numberOfRoadsOnMap[FrameName.GRAVEL_ROAD];
				if (calculation <= 0) {
					useEmitter().emit(EmitterEvents.PhaserWarningMessage, {
						x: selectedTile!.x,
						y: selectedTile!.y,
						text: "ui.gravel_road_limit",
					});
					useEmitter().emit(EmitterEvents.PhaserWarningMessage, {
						x: selectedTile!.x,
						y: selectedTile!.y,
						text: "ui.increase_more_towns_to_level",
						replace: { placeholder: "{level}", value: "10" },
						messageNumber: 2,
					});
					return;
				}
			} else if (
				tilerepoCobbledRoadsFrameNames.has(nextBuilding) ||
				tilerepoTunnelRoadsFrameNames.has(nextBuilding) ||
				tilerepoStoneBridgeRoadsFrameNames.has(nextBuilding)
			) {
				numberOfRoadsOnMap = tilerepo.getCurrentNumberOfPremiumRoads();
				userRoadCap = useSettlementsStore().getTotalAvailableRoadCaps;
				calculation =
					userRoadCap[FrameName.COBBLED_ROAD] -
					numberOfRoadsOnMap[FrameName.COBBLED_ROAD];
				if (calculation <= 0) {
					useEmitter().emit(EmitterEvents.PhaserWarningMessage, {
						x: selectedTile!.x,
						y: selectedTile!.y,
						text: "ui.cobbled_road_limit",
					});
					useEmitter().emit(EmitterEvents.PhaserWarningMessage, {
						x: selectedTile!.x,
						y: selectedTile!.y,
						text: "ui.increase_more_towns_to_level",
						replace: { placeholder: "{level}", value: "24" },
						messageNumber: 2,
					});
					return;
				}
			}
			const entity = tilerepo.createEntityByFrameName(nextBuilding);
			const hasLimitReached =
				!!entity?.buildingLimit &&
				entity.sameBuildingCount >= entity.buildingLimit;
			const isInvalidSalvageablePlacement =
				tilerepoSalvageableFrameNames.has(prevFrameName) &&
				isBuilding(nextBuilding) &&
				!canBuildOnTerrainBelow(tilerepo, selectedTile!, nextBuilding);
			if (
				(!(
					tilerepoSalvageableFrameNames.has(prevFrameName) &&
					(isRoad(nextBuilding) ||
						tilerepoForestFrameNames.has(nextBuilding) ||
						isBuilding(nextBuilding))
				) &&
					!selectedTile.canUpgradeTo.has(nextBuilding) &&
					!isRoad(nextBuilding)) ||
				isInvalidSalvageablePlacement ||
				hasLimitReached
			) {
				// ak sa neda budova postavit
				tilerepo.showNotPossibleBuildMessage(
					nextBuilding,
					selectedTile,
					hasLimitReached,
				);
				return;
			}

			if (
				tilerepoSalvageableFrameNames.has(prevFrameName) &&
				!selectedTile.isDemolishDisabled
			) {
				if (
					tilerepo.handleUpgrade(
						prevFrameName,
						FrameName.NOT_SET,
						tilerepo.getTileEntityByTileCoordinates({
							x: selectedTile?.x,
							y: selectedTile?.y,
						})!,
						undefined,
					) === null
				) {
					return;
				} else {
					selectedTile =
						tilerepo.getTileEntityByTileCoordinates({
							x: selectedTile?.x,
							y: selectedTile?.y,
						}) ?? selectedTile;
				}
			}
		}

		if (
			((action === QuickToolActions.DEMOLISH ||
				action === QuickToolActions.PUT_IN_INVENTORY) &&
				tilerepoSalvageableFrameNames.has(prevFrameName)) ||
			((tilerepoBanditsFrameNames.has(prevFrameName) ||
				tilerepoPiratesFrameNames.has(prevFrameName)) &&
				action !== QuickToolActions.DEFEAT &&
				action !== QuickToolActions.HIRE)
		) {
			return;
		}

		if (
			[
				QuickToolActions.SAVE_MATERIAL,
				QuickToolActions.HIRE,
				QuickToolActions.TRADE,
				QuickToolActions.CLEAR,
				QuickToolActions.DEMOLISH,
				QuickToolActions.BUILD,
				QuickToolActions.PUT_IN_INVENTORY,
			].includes(action) &&
			tilerepoSalvageableFrameNames.has(selectedTile.frameName) &&
			selectedTile?.requirements.has(ResourceType.LEVEL) &&
			(selectedTile?.requirements.get(ResourceType.LEVEL) ?? 0) >
				useSettlementsStore().mainLevel
		) {
			useEmitter().emit(EmitterEvents.PhaserWarningMessage, {
				x: selectedTile!.x,
				y: selectedTile!.y,
				text: "ui.not_enough_level",
				messageTime: 1800,
			});
			return;
		}

		if (
			(action === QuickToolActions.REMOVE &&
				tilerepoSalvageableFrameNames.has(prevFrameName)) ||
			((tilerepoBanditsFrameNames.has(prevFrameName) ||
				tilerepoPiratesFrameNames.has(prevFrameName)) &&
				action !== QuickToolActions.DEFEAT &&
				action !== QuickToolActions.HIRE)
		)
			return;

		if (
			action === QuickToolActions.DEFEAT &&
			!tilerepoBanditsFrameNames.has(prevFrameName) &&
			!tilerepoPiratesFrameNames.has(prevFrameName)
		) {
			return;
		}

		if (
			action === QuickToolActions.HIRE &&
			(tilerepoBanditsFrameNames.has(prevFrameName) ||
				tilerepoPiratesFrameNames.has(prevFrameName))
		) {
			if (!selectedTile?.skipDefeatGemsPrice) return;

			if (selectedTile.skipDefeatGemsPrice > useResourcesStore().gems) {
				useEmitter().emit(EmitterEvents.PhaserWarningMessage, {
					x: selectedTile!.x,
					y: selectedTile!.y,
					text: "ui.not_enough_gems",
					messageTime: 1800,
				});
				return;
			}

			tilerepo.handleUpgrade(
				selectedTile.frameName,
				FrameName.NOT_SET,
				selectedTile,
				true,
			);
		}

		if (
			quickToolActionsSalvage.has(action) &&
			!tilerepoSalvageableFrameNames.has(prevFrameName)
		)
			return;

		if (
			action === QuickToolActions.HIRE &&
			!tilerepoMercenariesFrameNames.has(prevFrameName)
		)
			return;

		if (prevFrameName !== building) {
			const gemsPrice = selectedTile.resolveDynamicResources({
				[ResourceType.GEMS]:
					selectedTile?.upgrades?.get(nextBuilding)?.price?.[
						ResourceType.GEMS
					],
			});

			const isPremiumBuilding =
				!!tilerepoPremiumBuildings.has(nextBuilding) &&
				!!gemsPrice[ResourceType.GEMS];
			const popupStore = usePopupStore();
			const buildingsStorageStore = useBuildingsStorageStore();
			const { showConfirmGemsMessages } = useSettingsStore();
			const entity = tilerepo.createEntityByFrameName(nextBuilding);

			if (
				isPremiumBuilding &&
				showConfirmGemsMessages &&
				buildingsStorageStore?.[nextBuilding] === 0 &&
				entity
			) {
				popupStore.showPopup({
					id: PopupDictionary.QUICK_TOOL_PREMIUM_BUILDING,
					type: PopupType.INFO,
					heading: translate("ui.premium_building"),
					body: {
						ctaText: translate("ui.building_confirm_message"),
						ctaText2: entity.name,
						price: gemsPrice,
					},
					skin: SkinTypes.PREMIUM,
					showSkipMessageNotice: true,
					actions: [
						{
							label: translate("button.buy"),
							buttonType: StatusTypes.PRIMARY,
							callback: async (): Promise<void> => {
								if (!selectedTile) return;

								tilerepo.handleUpgrade(
									selectedTile.frameName,
									nextBuilding,
									selectedTile,
									undefined,
									action ===
										QuickToolActions.PUT_IN_INVENTORY,
								);

								popupStore.hidePopup();
							},
						},
						{
							label: translate("button.cancel"),
							buttonType: StatusTypes.DEFAULT,
							callback: async (): Promise<void> => {
								popupStore.hidePopup();
							},
						},
					],

					maxHeight: 20,
					isCentered: true,
					isPaddingless: true,
				});
				return;
			}

			if (isRoad(nextBuilding)) {
				if (
					highwayStartCoordinates === null ||
					(highwayStartCoordinates?.x !== selectedTile.x &&
						highwayStartCoordinates?.y !== selectedTile.y)
				) {
					setHighwayStartCoordinates({
						x: selectedTile.x,
						y: selectedTile.y,
					});
				} else {
					buildHighway(tilerepo, selectedTile);
					return;
				}
			}
			if (
				tilerepo.handleUpgrade(
					selectedTile.frameName,
					nextBuilding,
					selectedTile,
					undefined,
					action === QuickToolActions.PUT_IN_INVENTORY,
				) === null &&
				nextBuilding !== FrameName.NOT_SET
			) {
				// ak sa neda budova postavit
				tilerepo.showNotPossibleBuildMessage(
					nextBuilding,
					selectedTile,
				);
			}
		}
	}
};

export const turnOnQuickTool = (
	prevFrameName: FrameName,
	nextFrameName: FrameName,
	selectedTile: TileEntity | null = null,
	isSkip: boolean = false,
	storeToInventory: boolean = false,
): void => {
	const {
		building,
		setBuilding,
		setActive,
		setShow,
		setAction,
		setHighwayStartCoordinates,
	} = useQuickToolStore();
	if (
		!(useUpgradesStore().getCurrentLevelById(UpgradeIds.quick_tool) > 0) ||
		!tilerepoQuickToolFrameNames.has(nextFrameName)
	)
		return;
	if (building !== nextFrameName) {
		setActive(false);
		setHighwayStartCoordinates(null);
	}
	setShow(true);
	const isCobbledRoad = tilerepoStoneBridgeRoadsFrameNames.has(nextFrameName);
	const isGravelRoad = tilerepoWoodenBridgeRoadsFrameNames.has(nextFrameName);
	const isDirtRoad = tilerepoFordCrossingRoadsFrameNames.has(nextFrameName);
	let nextBuilding = nextFrameName;
	if (isCobbledRoad) nextBuilding = FrameName.COBBLED_ROAD;
	if (isGravelRoad) nextBuilding = FrameName.GRAVEL_ROAD;
	if (isDirtRoad) nextBuilding = FrameName.DIRT_ROAD;

	setBuilding(nextBuilding);
	if (isBuilding(prevFrameName) || isRoad(prevFrameName)) {
		if (storeToInventory) {
			setAction(QuickToolActions.PUT_IN_INVENTORY);
		} else {
			setAction(QuickToolActions.DEMOLISH);
		}
	}
	if (tilerepoSalvageableFrameNames.has(prevFrameName)) {
		const { action } = selectedTile as AbstractSalvageableTileEntity;
		if (!action) return;
		if (action === TileAction.SAVE_MATERIAL) {
			setAction(QuickToolActions.SAVE_MATERIAL);
		} else if (action === TileAction.HIRE) {
			setAction(QuickToolActions.HIRE);
		} else if (action === TileAction.TRADING) {
			setAction(QuickToolActions.TRADE);
		} else if (action === TileAction.CLEAR) {
			setAction(QuickToolActions.CLEAR);
		}
	}
	if (
		isBuilding(nextFrameName) ||
		isRoad(nextFrameName) ||
		tilerepoForestFrameNames.has(nextFrameName)
	) {
		if (isRoad(nextFrameName) && selectedTile) {
			setHighwayStartCoordinates({
				x: selectedTile.x,
				y: selectedTile.y,
			});
		}
		setAction(QuickToolActions.BUILD);
		return;
	} else if (
		tilerepoBanditsFrameNames.has(prevFrameName) ||
		tilerepoPiratesFrameNames.has(prevFrameName)
	) {
		if (isSkip) {
			setAction(QuickToolActions.HIRE);
		} else {
			setAction(QuickToolActions.DEFEAT);
		}
	} else if (tilerepoForestFrameNames.has(prevFrameName)) {
		setAction(QuickToolActions.CUT);
	} else if (tilerepoGrasslandFrameNames.has(nextFrameName)) {
		setAction(QuickToolActions.REMOVE);
	}
};

export const buildHighway = (
	tilerepo: TileRepository,
	selectedTile: TileEntity,
): void => {
	const { highwayStartCoordinates, setHighwayStartCoordinates } =
		useQuickToolStore();
	const startCoordinates = highwayStartCoordinates;
	if (startCoordinates === null) return;
	const endCoordinates = { x: selectedTile.x, y: selectedTile.y };
	if (!tilerepo.isValidHighwayPath(startCoordinates, endCoordinates)) return;
	tilerepo.buildHighway(startCoordinates, endCoordinates);
	setHighwayStartCoordinates(endCoordinates);
};

export const getCorrectRoadFrameNameByPrevFrameName = (
	building: FrameName,
	prevFrameName: FrameName,
): FrameName => {
	let nextBuilding = building;
	if (isRoad(building)) {
		if (
			tilerepoRiversFrameNames.has(prevFrameName) ||
			tilerepoBridgeRoadsFrameNames.has(prevFrameName)
		) {
			const isCobbledBridge =
				tilerepoCobbledRoadsFrameNames.has(building);
			const isGravelBridge = tilerepoGravelRoadsFrameNames.has(building);
			const isDirtBridge = tilerepoDirtRoadsFrameNames.has(building);

			if (isCobbledBridge) nextBuilding = FrameName.STONE_BRIDGE;
			if (isGravelBridge) nextBuilding = FrameName.WOODEN_BRIDGE;
			if (isDirtBridge) nextBuilding = FrameName.FORD_CROSSING;
		}

		if (prevFrameName === FrameName.HILLS)
			nextBuilding = FrameName.HILLY_ROAD;
		else if (prevFrameName === FrameName.MOUNTAINS)
			nextBuilding = FrameName.MOUNTAIN_ROAD;
	}
	return nextBuilding;
};
